import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store';
import { getAdminUsage } from '../../api';
import { AdminUsageSummary } from '../../types';
import { AdminLayout } from '../../components/admin/AdminLayout';

// Helper type for the nested record value
type ProviderStats = { request_count: number; tokens: number; cost_micros: number };

export function AdminUsage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuthStore();
  const [usage, setUsage] = useState<AdminUsageSummary[]>([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !user?.is_admin)) {
      navigate('/', { replace: true });
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  useEffect(() => {
    if (isAuthenticated && user?.is_admin) {
      loadUsage();
    }
  }, [isAuthenticated, user, days]);

  const loadUsage = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAdminUsage(days);
      setUsage(data.usage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load usage');
    } finally {
      setLoading(false);
    }
  };

  const formatCost = (micros: number) => `$${(micros / 1_000_000).toFixed(4)}`;

  // Calculate totals
  const totals = usage.reduce((acc, day) => ({
    requests: acc.requests + day.request_count,
    promptTokens: acc.promptTokens + day.total_prompt_tokens,
    completionTokens: acc.completionTokens + day.total_completion_tokens,
    tokens: acc.tokens + day.total_tokens,
    cost: acc.cost + day.total_cost_micros,
  }), { requests: 0, promptTokens: 0, completionTokens: 0, tokens: 0, cost: 0 });

  // Aggregate by provider and request type
  const byProvider: Record<string, { requests: number; tokens: number; cost: number }> = {};
  const byType: Record<string, { requests: number; tokens: number; cost: number }> = {};
  
  usage.forEach(day => {
    (Object.entries(day.by_provider) as [string, ProviderStats][]).forEach(([provider, stats]) => {
      if (!byProvider[provider]) byProvider[provider] = { requests: 0, tokens: 0, cost: 0 };
      byProvider[provider].requests += stats.request_count;
      byProvider[provider].tokens += stats.tokens;
      byProvider[provider].cost += stats.cost_micros;
    });
    (Object.entries(day.by_request_type) as [string, ProviderStats][]).forEach(([type, stats]) => {
      if (!byType[type]) byType[type] = { requests: 0, tokens: 0, cost: 0 };
      byType[type].requests += stats.request_count;
      byType[type].tokens += stats.tokens;
      byType[type].cost += stats.cost_micros;
    });
  });

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-900">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (!user?.is_admin) return null;

  return (
    <AdminLayout
      title="Usage Analytics"
      breadcrumbs={[{ label: 'Admin', to: '/admin' }, { label: 'Usage' }]}
      actions={
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="bg-dark-700 border border-dark-600 rounded px-3 py-2 text-dark-50"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      }
    >
      <div className="space-y-6">
        {error && (
          <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <StatCard title="Total Requests" value={totals.requests.toLocaleString()} />
          <StatCard title="Prompt Tokens" value={totals.promptTokens.toLocaleString()} />
          <StatCard title="Completion Tokens" value={totals.completionTokens.toLocaleString()} />
          <StatCard title="Total Tokens" value={totals.tokens.toLocaleString()} />
          <StatCard title="Total Cost" value={formatCost(totals.cost)} highlight />
        </div>

        {/* By Provider */}
        <div className="bg-dark-800 border border-dark-600 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-dark-50 mb-4">By Provider</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(byProvider).map(([provider, stats]) => (
              <div key={provider} className="bg-dark-700 rounded p-4">
                <h4 className="font-medium text-primary-400 mb-2 capitalize">{provider}</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-dark-400">Requests</span>
                    <span className="text-dark-200">{stats.requests.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-dark-400">Tokens</span>
                    <span className="text-dark-200">{stats.tokens.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-dark-400">Cost</span>
                    <span className="text-green-400">{formatCost(stats.cost)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* By Request Type */}
        <div className="bg-dark-800 border border-dark-600 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-dark-50 mb-4">By Request Type</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(byType).map(([type, stats]) => (
              <div key={type} className="bg-dark-700 rounded p-4">
                <h4 className="font-medium text-dark-50 mb-2">{type}</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-dark-400">Requests</span>
                    <span className="text-dark-200">{stats.requests.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-dark-400">Tokens</span>
                    <span className="text-dark-200">{stats.tokens.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-dark-400">Cost</span>
                    <span className="text-green-400">{formatCost(stats.cost)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Daily Breakdown */}
        <div className="bg-dark-800 border border-dark-600 rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-dark-600">
            <h3 className="text-lg font-semibold text-dark-50">Daily Breakdown</h3>
          </div>
          <table className="w-full">
            <thead className="bg-dark-700">
              <tr>
                <th className="text-left px-4 py-3 text-dark-300 font-medium">Date</th>
                <th className="text-right px-4 py-3 text-dark-300 font-medium">Requests</th>
                <th className="text-right px-4 py-3 text-dark-300 font-medium">Prompt</th>
                <th className="text-right px-4 py-3 text-dark-300 font-medium">Completion</th>
                <th className="text-right px-4 py-3 text-dark-300 font-medium">Total</th>
                <th className="text-right px-4 py-3 text-dark-300 font-medium">Cost</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-dark-400">Loading...</td>
                </tr>
              ) : usage.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-dark-400">No usage data</td>
                </tr>
              ) : (
                usage.map((day) => (
                  <tr key={day.date} className="border-t border-dark-600">
                    <td className="px-4 py-3 text-dark-200">{day.date}</td>
                    <td className="px-4 py-3 text-dark-200 text-right">{day.request_count.toLocaleString()}</td>
                    <td className="px-4 py-3 text-dark-200 text-right">{day.total_prompt_tokens.toLocaleString()}</td>
                    <td className="px-4 py-3 text-dark-200 text-right">{day.total_completion_tokens.toLocaleString()}</td>
                    <td className="px-4 py-3 text-dark-200 text-right">{day.total_tokens.toLocaleString()}</td>
                    <td className="px-4 py-3 text-green-400 text-right">{formatCost(day.total_cost_micros)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}

function StatCard({ title, value, highlight }: { title: string; value: string; highlight?: boolean }) {
  return (
    <div className="bg-dark-800 border border-dark-600 rounded-lg p-4">
      <div className="text-dark-400 text-sm mb-1">{title}</div>
      <div className={`text-2xl font-bold ${highlight ? 'text-green-400' : 'text-dark-50'}`}>{value}</div>
    </div>
  );
}
